import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import TextInput from '../../../components/inputs/textInput';
import { useSendGoogleInvite } from '../../../apiHooks/mutations/integrationMutations/googleMutations';
import { CustomButton } from '../../../components/buttons/customButton';
import LoadingSpinner from '../../../components/loadingSpinner/loadingSpinner';
import useGetGoogleIntegrationStatus from '../../../apiHooks/queries/integrations/google/useGetGoogleIntegrationStatus';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import ProviderNumberBoxes from '../../../components/providerNumberBoxes/providerNumberBoxes';
import useGetAdProviderDetails from '../../../apiHooks/queries/adProviderDetails/useGetAdProviderDetails';
import { GET_PROVIDER_DETAILS } from '../../../constants/pageIdentifiers';
import { AdProviderIDs } from '../../../constants/constants';
import useGetGoogleTerms from '../../../apiHooks/queries/googleTerms/getGoogleTerms';
import { useSyncUrlSuffix } from '../../../apiHooks/mutations/syncUrlSuffix/useSyncUrlSuffix';
import infoIcon from '../../../assets/icons/info.svg';
import syncIcon from '../../../assets/icons/sync.svg';

export const statusCard = (t: typeof i18next.t, isActive?: boolean, isLoadingStatus?: boolean) => {
  if (isLoadingStatus) {
    return <LoadingSpinner color="primary" size={20} />;
  }

  if (isActive) {
    return (
      <div
        className="flex h-[40px] items-center justify-center rounded-lg border border-green px-5 text-sm leading-[0]
         text-green"
      >
        {t('active')}
      </div>
    );
  }

  return (
    <div
      className="flex h-[40px] items-center justify-center rounded-lg border border-red px-5 text-sm leading-[0]
        text-red"
    >
      {t('notActive')}
    </div>
  );
};

const GoogleTabPanel = () => {
  const organizationSettings = useOrganizationSettingsStore((state) => state.organizationSettings);

  const syncUrlSuffixMutation = useSyncUrlSuffix();

  const {
    mutate: sendInvite,
    isLoading: isSendingInvite,
  } = useSendGoogleInvite();

  const {
    data: integrationStatus,
    isLoading: isLoadingIntegrationStatus,
  } = useGetGoogleIntegrationStatus();

  const { data: adProviderDetailsData, isFetching } = useGetAdProviderDetails(
    GET_PROVIDER_DETAILS,
    AdProviderIDs.GOOGLE,
    integrationStatus?.data.data.isGoogleIntegrationActive || false,
  );

  const { data: googleTerms } = useGetGoogleTerms('GET_GOOGLE_TERMS');

  const { t } = useTranslation();
  const [googleId, setGoogleId] = useState<string>(
    organizationSettings?.googleClientId ?? '',
  );
  const [showInfo, setShowInfo] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setShowInfo(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div>
      <div className="flex justify-between gap-5 rounded-xl border p-4">
        <div className="flex gap-5">
          <TextInput
            name="Google Id"
            value={googleId}
            label="Google ID"
            size="small"
            placeholder="123-123-1234"
            onChange={(e) => setGoogleId(e.target.value)}
          />
          <CustomButton
            disabled={googleId === '' || isSendingInvite}
            className="h-[40px] px-[20px] text-sm"
            onClick={() => {
              sendInvite(googleId);
            }}
          >
            {t('linkAccount')}
          </CustomButton>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs font-[500] text-primary">
            {t('accountStatus')}
          </p>
          {statusCard(t, integrationStatus?.data.data.isGoogleIntegrationActive, isLoadingIntegrationStatus)}
        </div>
      </div>
      {integrationStatus?.data.data.isGoogleIntegrationActive
      && (
        <div>
          <div className="mt-4 flex justify-between gap-4 rounded-xl border p-4 ">
            <ProviderNumberBoxes
              isLoading={isFetching}
              tracked
              stats={adProviderDetailsData?.data?.data?.ads_tracked || 0}
            />
            <ProviderNumberBoxes
              isLoading={isFetching}
              revenue
              stats={adProviderDetailsData?.data?.data?.revenue_tracked || 0}
            />
            <ProviderNumberBoxes
              isLoading={isFetching}
              leads
              stats={adProviderDetailsData?.data?.data?.leads_attributed || 0}
            />
          </div>
          <div className="mt-5 flex flex-row justify-between align-middle rounded-xl border p-4">
            {organizationSettings?.finalUrlSuffixUpdatedAt && (
            <div className="flex items-center gap-3 rounded-xl border p-3 text-sm">
              <p className="font-medium text-primary">{t('lastUrlSuffixSync')}</p>
              <p className="text-gray-3">
                {new Date(organizationSettings.finalUrlSuffixUpdatedAt).toLocaleString()}
              </p>
            </div>
            )}
            <div className="flex flex-row mt-2 gap-3">
              <CustomButton
                className="h-[40px] px-[20px] text-sm"
                onClick={() => {
                  syncUrlSuffixMutation.mutate(undefined, {
                    onSuccess: () => {
                      useOrganizationSettingsStore.setState((state) => ({
                        organizationSettings: {
                          ...state.organizationSettings,
                          finalUrlSuffixUpdatedAt: new Date().toISOString(),
                        },
                      }));
                    },
                  });
                }}
              >
                {t('syncUrlSuffix')}
              </CustomButton>
              <div ref={cardRef}>
                <button
                  className="h-[40px] px-[20px] text-sm font-medium text-primary underline hover:no-underline"
                  onClick={() => setShowInfo((prev) => !prev)}
                >
                  Learn More
                </button>
              </div>
            </div>
            {!organizationSettings?.finalUrlSuffixUpdatedAt && (
              <div className="border-yellow-200  flex w-[70%] flex-row rounded-xl border
              bg-[#FFF7E5] px-4 py-3 text-sm text-primary"
              >
                <div className="flex items-center gap-3">
                  <img src={syncIcon} alt="Info" className="mt-1 w-[30px]" />
                  <span className="flex flex-col text-primary">
                    <p className="text-sm font-medium">
                      {t('syncNow')}
                    </p>
                  </span>
                </div>
              </div>
            )}
          </div>

            {showInfo && (
            <div ref={cardRef} className="mt-2 rounded-xl border  border-blue-200 bg-blue-50 text-blue-900 shadow-lg">
              <div className="flex flex-row gap-2 px-6 py-5">
                <img src={infoIcon} alt="Info" className="mt-1 w-[30px]" />
                <div className="flex flex-col">
                  <p className="font-medium">{t('urlSuffixInformation')}</p>
                  <p className="text-sm font-light">
                    Use a URL suffix to automatically add tracking parameters (like UTM tags) to your Google Ads URLs.
                    This helps Digtective track campaigns and conversions accurately.
                  </p>
                </div>
              </div>
              <div className="ml-3 h-1 w-[98%] rounded-t-xl bg-blue-500" />
            </div>
            )}

          <div
            className={`mt-4 flex flex-col items-center justify-center overflow-hidden rounded-xl border text-sm ${
              !googleTerms?.data?.data.accepted
                ? 'bg-[#FFF7E5]  text-primary'
                : 'bg-[#E6F4EA]  text-primary'
            }`}
          >

            <div
              className={`flex w-full items-center justify-between gap-4 px-4 py-6 ${
                !googleTerms?.data?.data.accepted
                  ? 'bg-[#FFF7E5]   text-primary'
                  : 'bg-[#E6F4EA] text-primary'
              }`}
            >

              <div className="flex items-center gap-3">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.9997 13V7C10.9997 6.45 11.4497 6 11.9997 6C12.5497 6 12.9997 6.45 12.9997 7V13C12.9997 13.55 12.5497 14 11.9997 14C11.4497 14 10.9997 13.55 10.9997 13ZM11.9997 15C11.1697 15 10.4997 15.67 10.4997 16.5C10.4997 17.33 11.1697 18 11.9997 18C12.8297 18 13.4997 17.33 13.4997 16.5C13.4997 15.67 12.8297 15 11.9997 15ZM23.5797 19.88C22.8797 21.23 21.4097 22 19.5697 22H4.43974C2.58974 22 1.12974 21.23 0.429744 19.88C-0.280256 18.52 -0.080256 16.78 0.929744 15.32L8.96974 2.6C9.67974 1.58 10.7997 1 11.9997 1C13.1997 1 14.3197 1.58 14.9997 2.57L23.0797 15.34C24.0897 16.8 24.2797 18.53 23.5697 19.88H23.5797ZM21.4297 16.46C21.4297 16.46 21.4097 16.44 21.4097 16.42L13.3397 3.67C13.0497 3.26 12.5497 3 11.9997 3C11.4497 3 10.9497 3.26 10.6397 3.71L2.58974 16.42C1.96974 17.3 1.82974 18.26 2.18974 18.95C2.53974 19.63 3.33974 20 4.42974 20H19.5497C20.6397 20 21.4397 19.63 21.7897 18.95C22.1497 18.26 22.0097 17.3 21.4197 16.46H21.4297Z" fill={!googleTerms?.data?.data.accepted ? '#FF9F1C' : '#4C956C'} />
                </svg>
                <div>
                  <p className="font-medium">
                    {!googleTerms?.data?.data.accepted
                      ? 'Enhanced Conversions are OFF'
                      : 'You have accepted Customer data terms.'}
                  </p>
                  <p className="font-light">
                    {!googleTerms?.data?.data.accepted
                      ? 'Enhanced Conversions are OFF\n'
                      + 'Learn more about enhanced conversions or click \'Read More\' and follow the instructions to turn it on.\n'
                      + ' '
                      : 'Please make sure you turn on enhanced conversions and choose Google Ads Api. You can learn more on the button!\n'
                      + ' '}
                  </p>
                </div>
              </div>
              <a
                href="http://knowledge.digtective.com/google-enhanced-conversions"
                className="shrink-0 font-medium underline hover:no-underline"
              >
                Learn More
              </a>
            </div>
            <div
              className={`h-1 w-[98%] ${
                !googleTerms?.data?.data.accepted
                  ? 'rounded-t-xl bg-yellow'
                  : 'rounded-t-xl bg-green'
              }`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleTabPanel;
